import { useEffect, useState } from 'react';
import { Navigate, generatePath } from 'react-router';
import ErrorQueryNames from '../Errors/ErrorQueryNames';
import useSubmitEmailSetupAccessCode from './api/useSubmitEmailSetupAccessCode';
import EmailIntegrationOauthError from './EmailIntegrationOauthError';
import { LoadingOverlay } from '@/Components/shared';
import useQuery from '@/Hooks/UseQuery';
import RoutePath from '@/Routing/RoutePath';
import withTalentMeshUserRequired from '@/Routing/withTalentMeshUserRequired';

const EmailIntegrationPage = () => {
    const code = useQuery('code');
    const companyId = useQuery('state');
    const oauthError = useQuery('error');
    const oauthErrorCode = useQuery('error_code');
    const oauthErrorDescription = useQuery('error_description');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const { trigger, isMutating, error } = useSubmitEmailSetupAccessCode(companyId);

    useEffect(() => {
        if (companyId && code) {
            trigger(code);
            setIsSubmitted(true);
        }
    }, [companyId, code, trigger]);

    // Handle OAuth errors from the email provider (Google, Microsoft, etc.)
    if (oauthError) {
        return (
            <EmailIntegrationOauthError
                companyId={companyId}
                oauthErrorCode={oauthErrorCode}
                oauthErrorDescription={oauthErrorDescription}
            />
        );
    }

    // Handle missing required parameters
    if (!code || !companyId) {
        const errorTitle = 'Email Integration Failed';
        const errorDescription = 'Missing required parameters for email integration. Please try again.';

        return (
            <Navigate
                to={`${RoutePath.Error}?${ErrorQueryNames.ErrorTitle}=${encodeURIComponent(errorTitle)}&${
                    ErrorQueryNames.ErrorDescription
                }=${encodeURIComponent(errorDescription)}`}
                replace
            />
        );
    }

    // Show loading state while submitting the code
    if (isMutating || !isSubmitted) {
        return <LoadingOverlay />;
    }

    // Handle API submission errors
    if (error) {
        const errorTitle = 'Email Integration Failed';
        const errorDescription =
            'Failed to complete email integration. Please try again or contact support if the problem persists.';

        return (
            <Navigate
                to={`${RoutePath.Error}?${ErrorQueryNames.ErrorTitle}=${encodeURIComponent(errorTitle)}&${
                    ErrorQueryNames.ErrorDescription
                }=${encodeURIComponent(errorDescription)}`}
                replace
            />
        );
    }

    // Success - redirect to integrations page
    return <Navigate to={generatePath(RoutePath.AccountSettingsIntegrations, { companyId })} replace />;
};

export default withTalentMeshUserRequired(EmailIntegrationPage);
